import { useState, useEffect, useCallback } from 'react';
import { invoicesAPI, projectsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [formData, setFormData] = useState({
    invoice_number: '',
    project_id: '',
    project_name: '',
    client_name: '',
    client_poc: '',
    amount: '',
    tax: '',
    status: 'draft',
    line_items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    payment_terms: '',
    date: '',
    due_date: '',
  });

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await invoicesAPI.getAll();
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchProjects();
  }, [fetchInvoices, fetchProjects]);

  const handleProjectChange = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setFormData({
        ...formData,
        project_id: projectId,
        project_name: project.name,
        client_name: project.client_name,
      });
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      const quantity = parseFloat(newLineItems[index].quantity) || 0;
      const rate = parseFloat(newLineItems[index].rate) || 0;
      newLineItems[index].amount = quantity * rate;
    }
    
    setFormData({ ...formData, line_items: newLineItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { _rid: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, description: '', quantity: 1, rate: 0, amount: 0 }],
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      const newLineItems = formData.line_items.filter((_, i) => i !== index);
      setFormData({ ...formData, line_items: newLineItems });
    }
  };

  const calculateTotal = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
    return subtotal;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const subtotal = calculateTotal();
      const data = {
        ...formData,
        amount: subtotal,
        tax: parseFloat(formData.tax) || 0,
      };
      
      if (editingInvoice) {
        await invoicesAPI.update(editingInvoice.id, data);
        toast.success('Invoice updated successfully');
      } else {
        await invoicesAPI.create(data);
        toast.success('Invoice created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || 'Operation failed');
      } else {
        toast.error(detail || 'Operation failed');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await invoicesAPI.delete(id);
      toast.success('Invoice deleted successfully');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      invoice_number: invoice.invoice_number,
      project_id: invoice.project_id,
      project_name: invoice.project_name,
      client_name: invoice.client_name,
      client_poc: invoice.client_poc,
      amount: invoice.amount,
      tax: invoice.tax,
      status: invoice.status,
      line_items: invoice.line_items,
      payment_terms: invoice.payment_terms,
      date: invoice.date,
      due_date: invoice.due_date,
    });
    setDialogOpen(true);
  };

  const handleView = (invoice) => {
    setViewingInvoice(invoice);
    setViewDialogOpen(true);
  };

  const resetForm = () => {
    setEditingInvoice(null);
    setFormData({
      invoice_number: '',
      project_id: '',
      project_name: '',
      client_name: '',
      client_poc: '',
      amount: '',
      tax: '',
      status: 'draft',
      line_items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
      payment_terms: '',
      date: '',
      due_date: '',
    });
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading invoices...</div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="invoices-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Invoices
          </h1>
          <p className="text-muted-foreground mt-2">Track and manage your invoices</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2" data-testid="create-invoice-button">
              <Plus className="h-4 w-4" />
              New Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
            <DialogHeader>
              <DialogTitle>{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</DialogTitle>
              <DialogDescription>
                {editingInvoice ? 'Update invoice details' : 'Create a new invoice for a project'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invoice-number">Invoice Number</Label>
                  <Input
                    id="invoice-number"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    required
                    data-testid="invoice-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project">Project</Label>
                  <select
                    id="project"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={formData.project_id}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    required
                    data-testid="invoice-project-select"
                  >
                    <option value="">Select a project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name} - {project.client_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client-poc">Client POC</Label>
                  <Input
                    id="client-poc"
                    value={formData.client_poc}
                    onChange={(e) => setFormData({ ...formData, client_poc: e.target.value })}
                    required
                    data-testid="invoice-client-poc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    data-testid="invoice-status-select"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Line Items</Label>
                {formData.line_items.map((item, index) => (
                  <div key={item._rid || `li-${index}`} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        required
                        data-testid={`line-item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        required
                        data-testid={`line-item-quantity-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Rate"
                        value={item.rate}
                        onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                        required
                        data-testid={`line-item-rate-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        value={item.amount.toFixed(2)}
                        readOnly
                        className="bg-muted"
                        data-testid={`line-item-amount-${index}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        disabled={formData.line_items.length === 1}
                        data-testid={`remove-line-item-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Line Item
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Subtotal</Label>
                  <Input
                    type="number"
                    value={calculateTotal().toFixed(2)}
                    readOnly
                    className="bg-muted"
                    data-testid="invoice-subtotal"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax">Tax ($)</Label>
                  <Input
                    id="tax"
                    type="number"
                    step="0.01"
                    value={formData.tax}
                    onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
                    required
                    data-testid="invoice-tax-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment-terms">Payment Terms</Label>
                <Input
                  id="payment-terms"
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  placeholder="Net 30"
                  required
                  data-testid="invoice-payment-terms-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Invoice Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="invoice-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due-date">Due Date</Label>
                  <Input
                    id="due-date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                    data-testid="invoice-due-date-input"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="invoice-submit-button">
                  {editingInvoice ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {invoices.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">
              No invoices yet. Create your first invoice to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {invoices.map((invoice) => (
            <Card key={invoice.id} className="hover:shadow-md transition-shadow" data-testid={`invoice-card-${invoice.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-xl font-semibold">{invoice.invoice_number}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {invoice.client_name} - {invoice.project_name}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(invoice)}
                      data-testid={`view-invoice-${invoice.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(invoice)}
                      data-testid={`edit-invoice-${invoice.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(invoice.id)}
                      data-testid={`delete-invoice-${invoice.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Status</p>
                    <span
                      className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : invoice.status === 'sent'
                          ? 'bg-blue-100 text-blue-700'
                          : invoice.status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {invoice.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Amount</p>
                    <p className="text-sm font-medium mt-1">${invoice.amount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Tax</p>
                    <p className="text-sm font-medium mt-1">${invoice.tax.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Total</p>
                    <p className="text-sm font-bold mt-1">${invoice.total_amount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Due Date</p>
                    <p className="text-sm font-medium mt-1">{invoice.due_date}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="invoice-view-dialog">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {viewingInvoice && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Invoice Number</p>
                  <p className="text-sm font-medium mt-1">{viewingInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Status</p>
                  <span
                    className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
                      viewingInvoice.status === 'paid'
                        ? 'bg-green-100 text-green-700'
                        : viewingInvoice.status === 'sent'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {viewingInvoice.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Project</p>
                  <p className="text-sm font-medium mt-1">{viewingInvoice.project_name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Client</p>
                  <p className="text-sm font-medium mt-1">{viewingInvoice.client_name}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Line Items</p>
                <div className="border rounded-md">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-2 text-xs font-semibold">Description</th>
                        <th className="text-right p-2 text-xs font-semibold">Qty</th>
                        <th className="text-right p-2 text-xs font-semibold">Rate</th>
                        <th className="text-right p-2 text-xs font-semibold">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingInvoice.line_items.map((item, index) => (
                        <tr key={`view-li-${index}-${item.description?.slice(0, 10) || ''}`} className="border-t">
                          <td className="p-2 text-sm">{item.description}</td>
                          <td className="p-2 text-sm text-right">{item.quantity}</td>
                          <td className="p-2 text-sm text-right">${item.rate.toFixed(2)}</td>
                          <td className="p-2 text-sm text-right">${item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-2 border-t pt-4">
                <div className="flex justify-between">
                  <span className="font-medium">Subtotal:</span>
                  <span>${viewingInvoice.amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Tax:</span>
                  <span>${viewingInvoice.tax.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span>${viewingInvoice.total_amount.toLocaleString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Payment Terms</p>
                  <p className="text-sm font-medium mt-1">{viewingInvoice.payment_terms}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Client POC</p>
                  <p className="text-sm font-medium mt-1">{viewingInvoice.client_poc}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Invoice Date</p>
                  <p className="text-sm font-medium mt-1">{viewingInvoice.date}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Due Date</p>
                  <p className="text-sm font-medium mt-1">{viewingInvoice.due_date}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
